import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { X, Search, Calendar, CreditCard } from 'lucide-react';

const STORAGE_KEY = 'bge_onboarding_v1_seen';

const STEPS = [
  {
    icon: Search,
    title: 'Step 1 — Browse expert tutors',
    body: "Filter by accent, language, or response time. Every class is $5 (25 min) or $10 (50 min), and every tutor's profile shows their intro and ratings so you can pick with confidence.",
  },
  {
    icon: Calendar,
    title: 'Step 2 — Pick a time that works',
    body: 'Open a tutor and see real-time availability in your timezone. Hold a slot for 10 minutes while you decide — no commitment yet.',
  },
  {
    icon: CreditCard,
    title: 'Step 3 — Book and start learning',
    body: 'Pay securely with card or wallet. First lesson with most tutors is discounted — look for the amber "Try first at $X" badge.',
  },
];

/**
 * Lightweight 3-step onboarding modal for new students.
 * - Fires once per browser (localStorage flag) when a student is logged in.
 * - User can dismiss at any step.
 * - Skips entirely for tutors and admins.
 */
const OnboardingTour = ({ user }) => {
  const [step, setStep] = useState(-1);

  useEffect(() => {
    if (!user || user.role !== 'student') return;
    if (typeof window === 'undefined') return;
    const seen = localStorage.getItem(STORAGE_KEY);
    if (!seen) {
      // Slight delay so the dashboard has time to paint behind it
      const t = setTimeout(() => setStep(0), 800);
      return () => clearTimeout(t);
    }
  }, [user]);

  const close = () => {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch { /* ignore quota / private-mode */ }
    setStep(-1);
  };

  if (step < 0) return null;
  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  return (
    <div className="fixed inset-0 z-[1000] flex items-center justify-center glass-overlay p-4" data-testid="onboarding-tour-modal">
      <div className="relative glass-panel rounded-2xl max-w-md w-full p-6 sm:p-8 animate-in fade-in zoom-in-95">
        <button
          onClick={close}
          aria-label="Skip onboarding"
          className="absolute top-3 right-3 p-1 text-slate-400 hover:text-slate-700 rounded-full transition-colors"
          data-testid="onboarding-close-btn"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center mb-4">
          <Icon className="w-7 h-7 text-primary" />
        </div>

        <h2 className="text-xl font-heading font-semibold text-text-primary mb-2">
          {current.title}
        </h2>
        <p className="text-text-secondary text-[15px] leading-relaxed mb-6">
          {current.body}
        </p>

        {/* Progress dots */}
        <div className="flex items-center gap-1.5 mb-6">
          {STEPS.map((_, i) => (
            <span
              key={i}
              className={`h-1.5 rounded-full transition-all ${i === step ? 'w-6 bg-primary' : 'w-1.5 bg-slate-200'}`}
            />
          ))}
        </div>

        <div className="flex items-center justify-between">
          <button
            onClick={close}
            className="text-sm text-text-secondary hover:text-text-primary transition-colors"
            data-testid="onboarding-skip-btn"
          >
            Skip
          </button>
          <Button
            onClick={() => (isLast ? close() : setStep(step + 1))}
            className="rounded-full px-6"
            data-testid={isLast ? 'onboarding-finish-btn' : 'onboarding-next-btn'}
          >
            {isLast ? 'Get started' : 'Next'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
